import React, { useState } from 'react';
import './Services.css';
import {
    FaTooth,
    FaMicroscope,
    FaStethoscope,
    FaXRay,
    FaMoon,
    FaBrush,
    FaSun,
    FaShieldAlt,
    FaStar,
    FaGem,
    FaCrown,
    FaSmile,
    FaTools,
    FaUserMd,
    FaArrowRight,
    FaCheckCircle,
    FaRegGem
} from 'react-icons/fa';
import { GiTooth } from 'react-icons/gi';

// Import individual service images
import consultationImg from './assets/dental-consultation.avif';
import conservativeImg from './assets/dental-conservative.jpg';
import endodonticsImg from './assets/dental-endodontics.avif';
import prosthodonticsImg from './assets/dental-prosthodontics.jpg';
import implantsImg from './assets/dental-implants.avif';
import extractionImg from './assets/dental-extraction.jpg';
import orthodonticsImg from './assets/dental-orthodontics.avif';

const Services = () => {
    const [activeCategory, setActiveCategory] = useState('all');

    const servicesData = {
        consultation: {
            title: "Consultation",
            icon: <FaStethoscope />,
            image: consultationImg,
            items: [
                { name: "Comprehensive Oral Examination", description: "Complete dental checkup with digital X-rays", icon: <FaStar /> },
                { name: "X-ray (Digital)", description: "Low-radiation digital dental imaging", icon: <FaXRay /> },
                { name: "Night Guard", description: "Custom-fitted mouth guards for teeth grinding", icon: <FaMoon /> },
                { name: "Scaling & Polishing", description: "Professional deep cleaning and stain removal", icon: <FaBrush /> },
                { name: "Teeth Whitening", description: "Professional laser teeth whitening treatment", icon: <FaSun /> }
            ]
        },
        conservative: {
            title: "Conservative Dentistry",
            icon: <FaShieldAlt />,
            image: conservativeImg,
            items: [
                { name: "Restoration", description: "Natural-looking tooth-colored fillings", icon: <FaStar /> },
                { name: "Anterior Restoration", description: "Front teeth aesthetic restorations", icon: <FaSmile /> },
                { name: "Posterior Restoration", description: "Back teeth durable restorations", icon: <FaTools /> }
            ]
        },
        endodontics: {
            title: "Endodontics",
            icon: <FaTooth />,
            image: endodonticsImg,
            items: [
                { name: "Pulpotomy", description: "Removal of infected pulp in primary teeth", icon: <FaCheckCircle /> },
                { name: "Pulpectomy", description: "Complete root canal treatment for milk teeth", icon: <FaCheckCircle /> },
                { name: "RCT (Rotary)", description: "Advanced rotary endodontic treatment", icon: <FaCrown /> }
            ]
        },
        prosthodontics: {
            title: "Prosthodontics",
            icon: <FaCrown />,
            image: prosthodonticsImg,
            items: [
                { name: "Crowns & Bridges", description: "Fixed dental prosthetics", icon: <FaCrown /> },
                { name: "PFM Crowns", description: "Porcelain fused to metal crowns", icon: <FaGem /> },
                { name: "Metal Crowns", description: "Durable full metal crowns", icon: <FaTools /> },
                { name: "Zirconia Crowns", description: "Premium aesthetic ceramic crowns", icon: <FaRegGem /> },
                { name: "Acrylic Partial Denture", description: "Removable acrylic partial dentures", icon: <FaSmile /> },
                { name: "Complete Denture", description: "Full mouth acrylic dentures", icon: <FaSmile /> },
                { name: "Soft Base Denture", description: "Comfortable soft-lined dentures", icon: <FaMoon /> },
                { name: "Cast Partial Denture", description: "Metal framework partial dentures", icon: <FaStar /> }
            ]
        },
        implants: {
            title: "Dental Implants",
            icon: <GiTooth />,
            image: implantsImg,
            items: [
                { name: "Single Implant", description: "Single tooth replacement", icon: <FaStar /> },
                { name: "Multiple Implants", description: "Multiple teeth replacement", icon: <FaStar /> },
                { name: "Full Arch Implants", description: "Complete arch restoration", icon: <FaCrown /> },
                { name: "Implant Supported Dentures", description: "Snap-on dentures", icon: <FaSmile /> }
            ]
        },
        extraction: {
            title: "Oral Surgery",
            icon: <FaUserMd />,
            image: extractionImg,
            items: [
                { name: "Simple Extraction", description: "Routine tooth removal", icon: <FaTooth /> },
                { name: "Surgical Extraction", description: "Complex surgical removal", icon: <FaTools /> },
                { name: "Impaction Removal", description: "Wisdom tooth extraction", icon: <FaStar /> }
            ]
        },
        orthodontics: {
            title: "Orthodontics",
            icon: <GiTooth />,
            image: orthodonticsImg,
            items: [
                { name: "Conventional Braces", description: "Traditional metal braces", icon: <FaTools /> },
                { name: "Clear Aligners", description: "Invisible teeth straightening", icon: <FaStar /> }
            ]
        }
    };

    const categories = [
        { id: 'all', name: 'All Services', icon: <FaTooth /> },
        { id: 'consultation', name: 'Consultation', icon: <FaStethoscope /> },
        { id: 'conservative', name: 'Conservative', icon: <FaShieldAlt /> },
        { id: 'endodontics', name: 'Endodontics', icon: <FaTooth /> },
        { id: 'prosthodontics', name: 'Prosthodontics', icon: <FaCrown /> },
        { id: 'implants', name: 'Implants', icon: <GiTooth /> },
        { id: 'extraction', name: 'Extraction', icon: <FaUserMd /> },
        { id: 'orthodontics', name: 'Orthodontics', icon: <GiTooth /> }
    ];

    const filteredServices = activeCategory === 'all'
        ? servicesData
        : { [activeCategory]: servicesData[activeCategory] };

    return (
        <div className="services-page">
            {/* Hero Section */}
            <section className="services-hero">
                <div className="services-hero-overlay"></div>
                <div className="container">
                    <div className="services-hero-content">
                        <h1 className="services-hero-title">Our Premium Services</h1>
                        <p className="services-hero-subtitle">Advanced Dental Care Tailored to Your Needs</p>
                        <div className="services-hero-badges">
                            <span>✨ State-of-the-Art Technology</span>
                            <span>🦷 Experienced Specialists</span>
                            <span>⭐ Patient-Centered Care</span>
                        </div>
                    </div>
                </div>
            </section>

            {/* Category Filter */}
            <div className="services-categories">
                <div className="container">
                    <div className="categories-wrapper">
                        {categories.map((category) => (
                            <button
                                key={category.id}
                                className={`category-btn ${activeCategory === category.id ? 'active' : ''}`}
                                onClick={() => setActiveCategory(category.id)}
                            >
                                <span className="category-icon">{category.icon}</span>
                                <span className="category-name">{category.name}</span>
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            {/* Services Sections */}
            <div className="container services-container">
                {Object.entries(filteredServices).map(([key, service]) => (
                    <div key={key} className="service-category-section">
                        <div className="service-category-header">
                            <div className="service-category-icon">{service.icon}</div>
                            <div>
                                <h2 className="service-category-title">{service.title}</h2>
                                <div className="service-category-divider"></div>
                            </div>
                            <div className="service-card">
                                <div className="service-card-image">
                                    <img
                                        src={service.image}
                                        alt={service.name}
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="row g-4">
                            {service.items.map((item, idx) => (
                                <div className="col-lg-3 col-md-6" key={idx}>
                                    <div className="service-card">
                                        
                                        <div className="service-card-content">
                                            <h3 className="service-card-title">{item.name}</h3>
                                            <p className="service-card-description">{item.description}</p>
                                            {/*                                             
                                            <button className="service-card-btn">
                                                Learn More <FaArrowRight />
                                            </button>
*/}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                ))}
            </div>

            {/* CTA Section */}
            <div className="services-cta">
                <div className="container">
                    <div className="services-cta-content">
                        <h2>Ready to Transform Your Smile?</h2>
                        <p>Schedule your consultation today and experience premium dental care</p>
                        <button className="services-cta-btn">Book Appointment</button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Services;